import { memo, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconArrowsLeftRightTrailOutline18 as SwitchIcon,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { Avatar, Button, Dialog, Input, Popover } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useContactOptions } from "@/lib/use-entity-options"

interface MergeContactDialogProps {
	contactId: string
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	initialWithId?: string | null
}

interface ContactRow {
	id: string
	email: string
	name?: string | null
	imageUrl?: string | null
	companyId?: string | null
}

const isEmpty = (value: string | number | null | undefined) =>
	value === null || value === undefined || value === ""

const dash = <span className="text-tertiary">--</span>

const displayName = (row: ContactRow) => row.name || row.email

export const MergeContactDialog = memo(
	({
		contactId,
		isOpen,
		onOpenChange,
		initialWithId,
	}: MergeContactDialogProps) => {
		const navigate = useNavigate()

		const [confirmText, setConfirmText] = useState("")
		const [pickerOpen, setPickerOpen] = useState(false)
		const [query, setQuery] = useState("")
		const [withId, setWithId] = useState<string | null>(null)
		const [sourceIsCurrent, setSourceIsCurrent] = useState(true)

		useEffect(() => {
			if (isOpen && initialWithId) setWithId(initialWithId)
		}, [isOpen, initialWithId])

		const { contacts } = useContactOptions({ query, enabled: isOpen })
		const [current] = useQuery(queries.contactById(contactId), {
			enabled: isOpen,
		})
		const [other] = useQuery(queries.contactById(withId ?? ""), {
			enabled: isOpen && !!withId,
		})
		const companyIds = useMemo(
			() =>
				[current?.companyId, other?.companyId].filter(
					(id): id is string => !!id,
				),
			[current?.companyId, other?.companyId],
		)
		const [companies] = useQuery(queries.companiesByIds(companyIds), {
			enabled: isOpen && companyIds.length > 0,
		})

		const sourceContact = sourceIsCurrent ? current : other
		const targetContact = sourceIsCurrent ? other : current

		const [sourceRequests] = useQuery(
			queries.contactRequests(sourceContact?.id ?? ""),
		)
		const [targetRequests] = useQuery(
			queries.contactRequests(targetContact?.id ?? ""),
		)
		const [sourceUpvotes] = useQuery(
			queries.contactUpvotes(sourceContact?.id ?? ""),
		)
		const [targetUpvotes] = useQuery(
			queries.contactUpvotes(targetContact?.id ?? ""),
		)

		const mergeMutation = api.contact.merge.useMutation()

		const filtered = useMemo(() => {
			const term = query.trim().toLowerCase()
			return contacts.filter(
				(c) =>
					c.id !== contactId &&
					(term === "" ||
						(c.name ?? "").toLowerCase().includes(term) ||
						c.email.toLowerCase().includes(term)),
			)
		}, [contacts, contactId, query])

		const companyName = (id?: string | null) =>
			companies.find((c) => c.id === id)?.name ?? null

		const result: ContactRow | undefined = useMemo(() => {
			if (!sourceContact || !targetContact) return undefined
			const pick = <T extends string | number | null | undefined>(
				target: T,
				source: T,
			) => (isEmpty(target) ? source : target)
			return {
				id: targetContact.id,
				email: targetContact.email,
				name: pick(targetContact.name, sourceContact.name),
				imageUrl: pick(targetContact.imageUrl, sourceContact.imageUrl),
				companyId: pick(targetContact.companyId, sourceContact.companyId),
			}
		}, [sourceContact, targetContact])

		const resultRequests = sourceRequests.length + targetRequests.length
		const resultUpvotes = sourceUpvotes.length + targetUpvotes.length

		const confirmed = confirmText.trim().toLowerCase() === "merge"
		const canSubmit =
			!!sourceContact && !!targetContact && !mergeMutation.isLoading

		const close = () => {
			onOpenChange(false)
			setConfirmText("")
			setWithId(null)
			setQuery("")
			setSourceIsCurrent(true)
		}

		const onMerge = async () => {
			if (!sourceContact || !targetContact) {
				toast.error({
					title: "Select a person",
					description: "Pick a person to merge with first.",
				})
				return
			}
			if (!confirmed) {
				toast.error({
					title: "Type merge to confirm",
					description: 'Enter "merge" in the field to confirm this action.',
				})
				return
			}
			const sourceId = sourceContact.id
			const targetId = targetContact.id
			const targetName = displayName(targetContact)

			close()
			if (targetId !== contactId) {
				void navigate({
					to: "/people/$id",
					params: { id: targetId },
					replace: true,
				})
			}

			try {
				await mergeMutation.mutateAsync({ sourceId, targetId })
				analytics.track("merge-contact", { sourceId, targetId })
				toast.success({
					title: "People merged",
					description: `Merged into ${targetName}.`,
				})
			} catch (error) {
				toast.error({
					title: "Failed to merge",
					description:
						error instanceof Error ? error.message : "Something went wrong.",
				})
			}
		}

		const contactPickerContent = (
			<Command shouldFilter={false}>
				<CommandInput
					placeholder="Search people..."
					autoFocus
					value={query}
					onValueChange={setQuery}
				/>
				<CommandList className="max-h-none">
					<CommandGroup>
						<div style={{ height: Math.min(filtered.length, 8) * 36 }}>
							<FixedSizeList
								height={Math.min(filtered.length, 8) * 36}
								width="100%"
								itemCount={filtered.length}
								itemData={filtered}
								itemKey={(index, data) => data[index]?.id ?? index}
								itemSize={36}
								overscanCount={5}
							>
								{({ index, style }) => {
									const c = filtered[index]
									if (!c) return null
									return (
										<CommandItem
											value={c.id}
											style={style}
											onSelect={() => {
												setWithId(c.id)
												setPickerOpen(false)
												setQuery("")
											}}
										>
											<div className="flex min-w-0 items-center gap-2">
												<Avatar
													size="xs"
													name={c.name || c.email}
													image={c.imageUrl}
												/>
												<span className="flex-1 truncate">
													{c.name || c.email}
												</span>
											</div>
										</CommandItem>
									)
								}}
							</FixedSizeList>
						</div>
					</CommandGroup>
				</CommandList>
			</Command>
		)

		const contactChip = (row?: ContactRow) => (
			<div className="bg-muted/40 flex min-w-0 select-none items-center gap-2 rounded-md border px-3 py-2">
				{row ? (
					<>
						<Avatar
							size="xs"
							name={row.name || row.email}
							image={row.imageUrl}
						/>
						<span className="truncate text-sm font-medium">
							{displayName(row)}
						</span>
					</>
				) : (
					<span className="text-sm text-tertiary">Select a person</span>
				)}
			</div>
		)

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(open) => (open ? onOpenChange(true) : close())}
				size="lg"
				title="Merge people"
				description="Threads, upvotes, and properties from the first person move into the second. The first person is then deleted, along with their email address."
				footer={
					<div className="flex items-center gap-2">
						<Button variant="outline" onClick={close}>
							Cancel
						</Button>
						<Button disabled={!canSubmit} onClick={onMerge}>
							Merge
						</Button>
					</div>
				}
			>
				<div className="space-y-5">
					<div className="flex items-center gap-3">
						<div className="min-w-0 flex-1">{contactChip(sourceContact)}</div>
						<ArrowRight className="size-4 shrink-0 text-tertiary" />
						<div className="min-w-0 flex-1">
							{other ? (
								contactChip(targetContact)
							) : (
								<Popover
									className="w-72 p-0"
									open={pickerOpen}
									onOpenChange={setPickerOpen}
									align="start"
									content={contactPickerContent}
								>
									<button
										type="button"
										className="bg-muted/40 flex w-full min-w-0 select-none items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-tertiary hover:text-secondary"
									>
										Select a person
									</button>
								</Popover>
							)}
						</div>
						<Button
							variant="outline"
							size="sm"
							Icon={SwitchIcon}
							disabled={!other}
							onClick={() => setSourceIsCurrent((v) => !v)}
						>
							Switch
						</Button>
					</div>

					{other && (
						<MergeTable
							source={sourceContact}
							target={targetContact}
							result={result}
							companyName={companyName}
							counts={{
								requests: [
									sourceRequests.length,
									targetRequests.length,
									resultRequests,
								],
								upvotes: [
									sourceUpvotes.length,
									targetUpvotes.length,
									resultUpvotes,
								],
							}}
						/>
					)}

					{other && (
						<div className="space-y-2">
							<div className="text-sm">
								Type <span className="font-semibold">merge</span> to continue
							</div>
							<Input
								name="merge-confirm"
								value={confirmText}
								onChange={(e) => setConfirmText(e.target.value)}
								placeholder="merge"
								autoComplete="off"
								onKeyDown={(e) => {
									if (e.key === "Enter" && canSubmit) {
										e.preventDefault()
										void onMerge()
									}
								}}
							/>
						</div>
					)}
				</div>
			</Dialog>
		)
	},
)

const Row = ({
	label,
	merge,
	withValue,
	result,
}: {
	label: string
	merge: React.ReactNode
	withValue: React.ReactNode
	result: React.ReactNode
}) => (
	<tr className="border-t">
		<td className="py-2.5 pr-4 text-sm text-secondary">{label}</td>
		<td className="py-2.5 pr-4 text-sm">{merge}</td>
		<td className="py-2.5 pr-4 text-sm">{withValue}</td>
		<td className="py-2.5 text-sm">{result}</td>
	</tr>
)

const MergeTable = ({
	source,
	target,
	result,
	companyName,
	counts,
}: {
	source?: ContactRow
	target?: ContactRow
	result?: ContactRow
	companyName: (id?: string | null) => string | null
	counts: {
		requests: [number, number, number]
		upvotes: [number, number, number]
	}
}) => {
	const nameCell = (row?: ContactRow) =>
		row ? (
			<span className="flex min-w-0 items-center gap-2">
				<Avatar size="xs" name={row.name || row.email} image={row.imageUrl} />
				<span className="truncate">{displayName(row)}</span>
			</span>
		) : (
			dash
		)

	const text = (value?: string | null) => (isEmpty(value) ? dash : value)
	const companyCell = (row?: ContactRow) => text(companyName(row?.companyId))
	return (
		<table className="w-full border-collapse">
			<thead>
				<tr className="text-left text-sm font-medium text-secondary">
					<th className="pb-2 pr-4" />
					<th className="pb-2 pr-4">Merge</th>
					<th className="pb-2 pr-4">With</th>
					<th className="pb-2">Result</th>
				</tr>
			</thead>
			<tbody>
				<Row
					label="Name"
					merge={nameCell(source)}
					withValue={nameCell(target)}
					result={nameCell(result)}
				/>
				<Row
					label="Email"
					merge={text(source?.email)}
					withValue={text(target?.email)}
					result={text(result?.email)}
				/>
				<Row
					label="Company"
					merge={companyCell(source)}
					withValue={companyCell(target)}
					result={companyCell(result)}
				/>
				<Row
					label="Threads"
					merge={counts.requests[0]}
					withValue={counts.requests[1]}
					result={counts.requests[2]}
				/>
				<Row
					label="Upvotes"
					merge={counts.upvotes[0]}
					withValue={counts.upvotes[1]}
					result={counts.upvotes[2]}
				/>
			</tbody>
		</table>
	)
}
